// Shared option constants for module 4.1 (Public Tender Request Module)
// Centralizing to avoid duplication across form components
//
// i18n-aware: factory functions accept `lang` and return localized labels.
// Static exports are kept for backward compatibility.

import { i18n } from './i18n';
import { StringUtils } from '@/chassis/core/utils';

export interface Option<T = string> {
    label: string;
    value: T;
}

export interface I18nOption<T = string> {
    label: { pt: string; en: string; es: string };
    value: T;
}

type Lang = 'pt' | 'en' | 'es';
export const PUBLIC_TENDER_STATUS_VALUES = [
    'DRAFT',
    'AWAITING_PUBLICATION',
    'PUBLISHED',
    'RECEIVING_PROPOSALS',
    'ANALYZING_PROPOSALS',
    'AWAITING_DISPUTE',
    'IN_DISPUTE',
    'IN_ENABLEMENT',
    'IN_JUDGMENT',
    'IN_APPEAL',
    'AWARDING',
    'AWARDED',
    'HOMOLOGATED',
    'FINAL_RESULT',
    'CANCELLED',
    'FAILED',
    'SUSPENDED',
    'REVOKED',
    'ANNULLED',
    'UNDER_RECTIFICATION',
    'DESERTED',
] as const;

export type PublicTenderStatus = (typeof PUBLIC_TENDER_STATUS_VALUES)[number];

/** Resolve a single i18n entry to a string for the given language */
const txt = (entry: { pt: string; en: string; es: string }, lang: Lang): string => entry[lang] ?? entry.pt;

const slugify = (value: string) => StringUtils.slugify(value);

// =============================================================================
// i18n-aware option definitions
// =============================================================================

export const tenderStatusI18nOptions: I18nOption[] = [
    { label: i18n['options.tenderStatus.rascunho'], value: 'DRAFT' },
    { label: { pt: 'Aguardando publicação', en: 'Awaiting publication', es: 'Esperando publicación' }, value: 'AWAITING_PUBLICATION' },
    { label: i18n['options.tenderStatus.publicado'], value: 'PUBLISHED' },
    { label: i18n['options.tenderStatus.recebendoPropostas'], value: 'RECEIVING_PROPOSALS' },
    { label: i18n['options.tenderStatus.analisandoPropostas'], value: 'ANALYZING_PROPOSALS' },
    { label: { pt: 'Aguardando disputa', en: 'Awaiting dispute', es: 'Esperando disputa' }, value: 'AWAITING_DISPUTE' },
    { label: i18n['options.tenderStatus.emDisputa'], value: 'IN_DISPUTE' },
    { label: i18n['options.tenderStatus.habilitacao'], value: 'IN_ENABLEMENT' },
    { label: i18n['options.tenderStatus.adjudicado'], value: 'AWARDED' },
    { label: i18n['options.tenderStatus.homologado'], value: 'HOMOLOGATED' },
    { label: i18n['options.tenderStatus.cancelado'], value: 'CANCELLED' },
    { label: i18n['options.tenderStatus.fracassado'], value: 'FAILED' },
    // Bug 27/08 (Alexandre): 'Deserta' faltava no catálogo de opções — o filtro
    // de status listava 20 dos 21 status canônicos e não havia como buscar as
    // licitações desertas, que são o desfecho mais comum depois de julgamento.
    { label: i18n['options.tenderStatus.deserta'], value: 'DESERTED' },
    { label: i18n['options.tenderStatus.suspenso'], value: 'SUSPENDED' },
    { label: i18n['options.tenderStatus.revogado'], value: 'REVOKED' },
    { label: i18n['options.tenderStatus.anulado'], value: 'ANNULLED' },
    { label: i18n['options.tenderStatus.emJulgamento'], value: 'IN_JUDGMENT' },
    { label: { pt: 'Em recurso', en: 'In appeal', es: 'En recurso' }, value: 'IN_APPEAL' },
    { label: { pt: 'Adjudicando', en: 'Awarding', es: 'Adjudicando' }, value: 'AWARDING' },
    { label: i18n['options.tenderStatus.emRetificacao'], value: 'UNDER_RECTIFICATION' },
    { label: i18n['options.tenderStatus.resultadoFinal'], value: 'FINAL_RESULT' },
];

// BUG-SMOKE-01 (2026-04-20): values dos options agora são os enums EN canônicos
// que o backend persiste/retorna (ex.: ACQUISITION, ELECTRONIC_AUCTION, LOWEST_BID,
// OPEN). Antes eram PT em caixa alta com acentos ('AQUISIÇÃO', 'PREGÃO ELETRÔNICO'),
// e como o backend retorna EN, o <select> não batia o valor selecionado e mostrava
// "Selecione...". Labels PT continuam via i18n (não afeta a UI). Enum EN canônico:
// `src/domains/pages/4.1-publictender-module-request/PublicTenders/Auctions/Shared/tender-field-labels.ts`.
export const contractTypeI18nOptions: I18nOption[] = [
    { label: i18n['options.contractType.aquisicao'], value: 'ACQUISITION' },
    { label: i18n['options.contractType.registroDePreco'], value: 'PRICE_REGISTRY' },
    { label: i18n['options.contractType.aquisicaoParcelada'], value: 'PARTIAL_ACQUISITION' },
    { label: i18n['options.contractType.prestacaoDeServico'], value: 'SERVICE_PROVISION' },
];

export const fundingSourceI18nOptions: I18nOption[] = [
    { label: i18n['options.fundingSource.proprio'], value: 'OWN' },
    { label: i18n['options.fundingSource.convenio'], value: 'AGREEMENT' },
    { label: i18n['options.fundingSource.estadual'], value: 'STATE' },
    { label: i18n['options.fundingSource.federal'], value: 'FEDERAL' },
];

export const modalityI18nOptions: I18nOption[] = [
    { label: i18n['options.modality.pregaoEletronico'], value: 'ELECTRONIC_AUCTION' },
    // Pregao presencial (15/09/2026): mesmo rito do eletronico; a plataforma registra a sessao.
    { label: i18n['options.modality.pregaoPresencial'], value: 'PRESENTIAL_AUCTION' },
    { label: i18n['options.modality.disputaEletronica'], value: 'ELECTRONIC_DISPUTE' },
    { label: i18n['options.modality.concorrenciaEletronica'], value: 'ELECTRONIC_COMPETITION' },
    { label: i18n['options.modality.leilaoEletronico'], value: 'ELECTRONIC_BIDDING' },
    { label: i18n['options.modality.credenciamento'], value: 'ACCREDITATION' },
    { label: i18n['options.modality.dialogoCompetitivo'], value: 'COMPETITIVE_DIALOGUE' },
    { label: i18n['options.modality.inexigibilidadeEletronica'], value: 'ELECTRONIC_UNENFORCEABILITY' },
    // Dispensa Eletrônica (Art. 75 Lei 14.133 + IN SEGES/ME 67/2021) — Fase 1 (21/07/2026)
    { label: { pt: 'Dispensa Eletrônica', en: 'Electronic exemption', es: 'Dispensa electrónica' }, value: 'ELECTRONIC_EXEMPTION' },
];

// Cobertura completa dos 10 enums aceitos pela entity backend
// `opd-publictenders.entity.ts:31`. Se o backend persistiu um desses valores
// (ex.: em tenders legacy ou via import), o form precisa conseguir exibir o
// label — sem option, o select renderiza "Selecione..." e salvar o form pode
// zerar o campo silenciosamente. Fix 2026-04-20 adicionou LOWEST_BID e
// BEST_TECHNICAL_AND_PRICE (backend aceita ambos; frontend antes só tinha 8).
// Ver `frontend-enum-coverage-judgement-criteria.md` (completed).
export const judgementCriteriaI18nOptions: I18nOption[] = [
    { label: i18n['options.judgementCriteria.menorPreco'], value: 'LOWEST_PRICE' },
    { label: i18n['options.judgementCriteria.menorLance'], value: 'LOWEST_BID' },
    { label: i18n['options.judgementCriteria.melhorTecnica'], value: 'BEST_TECHNICAL' },
    { label: i18n['options.judgementCriteria.tecnicaEPreco'], value: 'TECHNIQUE_AND_PRICE' },
    { label: i18n['options.judgementCriteria.melhorTecnicaEPreco'], value: 'BEST_TECHNICAL_AND_PRICE' },
    { label: i18n['options.judgementCriteria.maiorDesconto'], value: 'HIGHEST_DISCOUNT' },
    { label: i18n['options.judgementCriteria.maiorRetornoEconomico'], value: 'HIGHEST_ECONOMIC_RETURN' },
    { label: i18n['options.judgementCriteria.maiorLance'], value: 'HIGHEST_BID' },
    { label: i18n['options.judgementCriteria.concurso'], value: 'CONTEST' },
    { label: i18n['options.judgementCriteria.melhorTecnicaOuConteudoArtistico'], value: 'BEST_ARTISTIC_CONTENT' },
];

export const disputeTypeI18nOptions: I18nOption[] = [
    { label: i18n['options.disputeType.aberto'], value: 'OPEN' },
    { label: i18n['options.disputeType.abertoFechado'], value: 'OPEN_THEN_CLOSED' },
    { label: i18n['options.disputeType.fechado'], value: 'CLOSED' },
    { label: i18n['options.disputeType.fechadoAberto'], value: 'CLOSED_THEN_OPEN' },
];

// =============================================================================
// Modality-specific field configuration (Art. 29, 33, 56 Lei 14.133/2021)
// =============================================================================

// BUG-SMOKE-01: keys e valores internos alinhados aos enums EN canônicos.
export type ModalityKey = 'ELECTRONIC_AUCTION' | 'PRESENTIAL_AUCTION' | 'ELECTRONIC_DISPUTE' | 'ELECTRONIC_COMPETITION' | 'ELECTRONIC_BIDDING' | 'ACCREDITATION' | 'ELECTRONIC_EXEMPTION';

export interface ModalityFieldConfig {
    allowedJudgementCriteria: string[];
    allowedDisputeTypes: string[];
    lockedFields: Record<string, string | number>;
    hiddenFields: string[];
}

export const MODALITY_FIELD_CONFIG: Record<ModalityKey, ModalityFieldConfig> = {
    ELECTRONIC_AUCTION: {
        allowedJudgementCriteria: ['LOWEST_PRICE', 'HIGHEST_DISCOUNT'],
        allowedDisputeTypes: ['OPEN', 'OPEN_THEN_CLOSED'],
        lockedFields: {},
        hiddenFields: [],
    },
    // Pregao presencial: a disputa e por rodadas conduzidas pelo pregoeiro na sessao
    // (sem cronometro), entao o inicio e sempre manual e o modo aberto.
    PRESENTIAL_AUCTION: {
        allowedJudgementCriteria: ['LOWEST_PRICE', 'HIGHEST_DISCOUNT'],
        allowedDisputeTypes: ['OPEN'],
        lockedFields: { disputeType: 'OPEN', disputeStartMode: 'MANUAL', batchSize: 'ALL' },
        hiddenFields: ['disputeType', 'disputeStartMode', 'batchSize', 'isPhaseInversion'],
    },
    ELECTRONIC_DISPUTE: {
        allowedJudgementCriteria: ['LOWEST_PRICE', 'HIGHEST_DISCOUNT'],
        allowedDisputeTypes: ['OPEN', 'OPEN_THEN_CLOSED'],
        lockedFields: {},
        hiddenFields: [],
    },
    ELECTRONIC_COMPETITION: {
        allowedJudgementCriteria: ['LOWEST_PRICE', 'HIGHEST_DISCOUNT', 'BEST_TECHNICAL', 'TECHNIQUE_AND_PRICE', 'HIGHEST_ECONOMIC_RETURN', 'BEST_ARTISTIC_CONTENT'],
        allowedDisputeTypes: ['OPEN', 'OPEN_THEN_CLOSED', 'CLOSED', 'CLOSED_THEN_OPEN'],
        lockedFields: {},
        hiddenFields: [],
    },
    ELECTRONIC_BIDDING: {
        allowedJudgementCriteria: ['HIGHEST_BID'],
        allowedDisputeTypes: ['OPEN'],
        lockedFields: { judgementCriteria: 'HIGHEST_BID', disputeType: 'OPEN' },
        hiddenFields: ['contractType', 'documentSubmissionMode', 'isPhaseInversion', 'documentSubmissionDeadlineHours', 'isHiddenBudget', 'requiredProposalDocuments', 'requiresProposalGuarantee'],
    },
    // Credenciamento (Art. 79 Lei 14.133): NAO tem disputa, ranking nem criterio
    // competitivo — cada candidatura e habilitada de forma independente. A entity
    // do backend ainda exige valores para os campos de disputa, entao travamos em
    // valores validos e neutros (o lifecycle roteia por modality==='ACCREDITATION',
    // nao por esses valores) e escondemos os campos do form. disputeType='CLOSED'
    // ainda garante, de brinde, que a Sala de Disputa nao apareca nas views.
    ACCREDITATION: {
        allowedJudgementCriteria: [],
        allowedDisputeTypes: [],
        lockedFields: {
            judgementCriteria: 'LOWEST_PRICE',
            disputeType: 'CLOSED',
            disputeStartMode: 'AUTO',
            batchSize: 'ALL',
        },
        hiddenFields: ['judgementCriteria', 'disputeType', 'disputeStartMode', 'batchSize', 'isPhaseInversion', 'isHiddenBudget', 'requiresProposalGuarantee'],
    },
    // Dispensa Eletrônica (Art. 75 + IN SEGES/ME 67/2021), v1 = COM disputa
    // (incisos I/II). A "janela de lances" da dispensa é assíncrona e será
    // implementada na Fase 2 roteando por modality (como o credenciamento);
    // disputeType fica travado em OPEN (lances abertos, menor lance visível)
    // e escondido do form — quem manda é a modalidade.
    ELECTRONIC_EXEMPTION: {
        allowedJudgementCriteria: ['LOWEST_PRICE', 'HIGHEST_DISCOUNT'],
        allowedDisputeTypes: ['OPEN'],
        // Dispensa simplificada (decisao do cliente 15/09/2026): sem fase recursal,
        // sem impugnacao e sem esclarecimento. Os prazos recursais seguem travados
        // no minimo que o backend exige (campos obrigatorios da entity), mas ficam
        // ocultos e nunca sao usados: o backend recusa recurso/impugnacao/
        // esclarecimento na dispensa e adjudica direto ao habilitar.
        lockedFields: {
            disputeType: 'OPEN', disputeStartMode: 'AUTO', batchSize: 'ALL',
            appealManifestationTime: 10, appealReceptionFormTimeHours: 24, counterArgumentsReceptionFormTimeHours: 24,
        },
        hiddenFields: [
            'disputeType', 'disputeStartMode', 'batchSize', 'isPhaseInversion',
            'appealManifestationTime', 'appealReceptionFormTimeHours', 'counterArgumentsReceptionFormTimeHours',
            'clarificationEndDate', 'impeachmentEndDate',
        ],
    },
};

export const supplierRegionalityBenefitI18nOptions: I18nOption[] = [
    { label: i18n['options.supplierRegionality.herdar'], value: 'HERDAR' },
    { label: i18n['options.supplierRegionality.nao'], value: 'NÃO' },
    { label: i18n['options.supplierRegionality.preferenciaEstadual'], value: 'PREFERÊNCIA ESTADUAL' },
    { label: i18n['options.supplierRegionality.exclusivoEstadual'], value: 'EXCLUSIVO ESTADUAL' },
    { label: i18n['options.supplierRegionality.preferenciaMunicipal'], value: 'PREFERÊNCIA MUNICIPAL' },
    { label: i18n['options.supplierRegionality.exclusivoMunicipal'], value: 'EXCLUSIVO MUNICIPAL' },
];

export const supplierSizeBenefitI18nOptions: I18nOption[] = [
    { label: i18n['options.supplierSize.herdar'], value: 'HERDAR' },
    { label: i18n['options.supplierSize.nao'], value: 'NÃO' },
    { label: i18n['options.supplierSize.preferencia'], value: 'PREFERÊNCIA ME/EPP/COOP' },
    { label: i18n['options.supplierSize.exclusivo'], value: 'EXCLUSIVO ME/EPP/COOP' },
];

/**
 * S-T9 — Formato de lance do lote. Os valores casam com o que o backend le
 * (`realtimebidding.use-case.ts`: `bidFormat.toUpperCase() === 'ITEM'`) e com o
 * enum documentado na API externa (GLOBAL/ITEM/KIT). O seletor saiu do formulario
 * em abril e voltou em 26/08, quando o primeiro edital de Pato Branco definiu
 * disputa por valor unitario do item.
 *
 * Vazio = comportamento atual (lance unico do lote) — `auction-mappers.ts` so
 * deriva 'per-item' quando o valor e exatamente ITEM.
 */
export const bidFormatI18nOptions: I18nOption[] = [
    { label: i18n['publicTenders.lots.form.fields.bidFormat.options.global'], value: 'GLOBAL' },
    { label: i18n['publicTenders.lots.form.fields.bidFormat.options.item'], value: 'ITEM' },
    { label: i18n['publicTenders.lots.form.fields.bidFormat.options.kit'], value: 'KIT' },
];

// =============================================================================
// Factory functions: return Option[] with resolved labels for a given language
// =============================================================================

const resolveOptions = <T = string>(i18nOpts: I18nOption<T>[], lang: Lang): Option<T>[] =>
    i18nOpts.map((o) => ({ label: txt(o.label, lang), value: o.value }));

export const getTenderStatusOptions = (lang: Lang): Option[] => resolveOptions(tenderStatusI18nOptions, lang);
export const getContractTypeOptions = (lang: Lang): Option[] => resolveOptions(contractTypeI18nOptions, lang);
export const getFundingSourceOptions = (lang: Lang): Option[] => resolveOptions(fundingSourceI18nOptions, lang);
export const getModalityOptions = (lang: Lang): Option[] => resolveOptions(modalityI18nOptions, lang);
export const getJudgementCriteriaOptions = (lang: Lang): Option[] => resolveOptions(judgementCriteriaI18nOptions, lang);
export const getDisputeTypeOptions = (lang: Lang): Option[] => resolveOptions(disputeTypeI18nOptions, lang);
export const getSupplierRegionalityBenefitOptions = (lang: Lang): Option[] => resolveOptions(supplierRegionalityBenefitI18nOptions, lang);
export const getSupplierSizeBenefitOptions = (lang: Lang): Option[] => resolveOptions(supplierSizeBenefitI18nOptions, lang);
export const getBidFormatOptions = (lang: Lang): Option[] => resolveOptions(bidFormatI18nOptions, lang);

// =============================================================================
// Backward-compatible static exports (Portuguese labels - deprecated)
// =============================================================================

/** @deprecated Use getTenderStatusOptions(lang) instead */
export const tenderStatusOptions: Option[] = resolveOptions(tenderStatusI18nOptions, 'pt');
/** @deprecated Use getContractTypeOptions(lang) instead */
export const contractTypeOptions: Option[] = resolveOptions(contractTypeI18nOptions, 'pt');
/** @deprecated Use getFundingSourceOptions(lang) instead */
export const fundingSourceOptions: Option[] = resolveOptions(fundingSourceI18nOptions, 'pt');
/** @deprecated Use getModalityOptions(lang) instead */
export const modalityOptions: Option[] = resolveOptions(modalityI18nOptions, 'pt');
/** @deprecated Use getJudgementCriteriaOptions(lang) instead */
export const judgementCriteriaOptions: Option[] = resolveOptions(judgementCriteriaI18nOptions, 'pt');
/** @deprecated Use getDisputeTypeOptions(lang) instead */
export const disputeTypeOptions: Option[] = resolveOptions(disputeTypeI18nOptions, 'pt');
/** @deprecated Use getSupplierRegionalityBenefitOptions(lang) instead */
export const supplierRegionalityBenefitOptions: Option[] = resolveOptions(supplierRegionalityBenefitI18nOptions, 'pt');
/** @deprecated Use getSupplierSizeBenefitOptions(lang) instead */
export const supplierSizeBenefitOptions: Option[] = resolveOptions(supplierSizeBenefitI18nOptions, 'pt');

// =============================================================================
// Status and Modality Metadata (for PublicTenders list/display)
// =============================================================================

export type StatusKey = 'draft' | 'published' | 'in_progress' | 'finalized' | 'suspended' | 'cancelled';

export const STATUS_META: Record<StatusKey, { label: string; labelKey: string; color: string; bgColor?: string }> = {
    draft: { label: 'Rascunho', labelKey: 'publicTenders.status.draft', color: 'text-slate-700', bgColor: 'bg-slate-100' },
    published: { label: 'Publicada', labelKey: 'publicTenders.status.published', color: 'text-primary', bgColor: 'bg-primary/15' },
    in_progress: { label: 'Em Andamento', labelKey: 'publicTenders.status.in_progress', color: 'text-warning', bgColor: 'bg-warning/15' },
    finalized: { label: 'Finalizada', labelKey: 'publicTenders.status.finalized', color: 'text-success', bgColor: 'bg-success/15' },
    suspended: { label: 'Suspensa', labelKey: 'publicTenders.status.suspended', color: 'text-danger', bgColor: 'bg-danger/15' },
    cancelled: { label: 'Cancelada', labelKey: 'publicTenders.status.cancelled', color: 'text-slate-600', bgColor: 'bg-slate-300' },
};

const PUBLIC_TENDER_STATUS_SET = new Set<string>(PUBLIC_TENDER_STATUS_VALUES);

const PUBLIC_TENDER_STATUS_GROUPS: Record<PublicTenderStatus, StatusKey> = {
    DRAFT: 'draft',
    AWAITING_PUBLICATION: 'draft',
    PUBLISHED: 'published',
    RECEIVING_PROPOSALS: 'in_progress',
    ANALYZING_PROPOSALS: 'in_progress',
    AWAITING_DISPUTE: 'in_progress',
    IN_DISPUTE: 'in_progress',
    IN_ENABLEMENT: 'in_progress',
    IN_JUDGMENT: 'in_progress',
    IN_APPEAL: 'in_progress',
    AWARDING: 'in_progress',
    AWARDED: 'finalized',
    HOMOLOGATED: 'finalized',
    FINAL_RESULT: 'finalized',
    CANCELLED: 'cancelled',
    FAILED: 'cancelled',
    SUSPENDED: 'suspended',
    REVOKED: 'cancelled',
    ANNULLED: 'cancelled',
    UNDER_RECTIFICATION: 'in_progress',
    DESERTED: 'cancelled',
};

export const PUBLIC_TENDER_STATUS_META: Record<
    PublicTenderStatus,
    {
        label: string;
        labelKey: string;
        dotColor: string;
        textColor: string;
        bgColor: string;
        borderColor: string;
    }
> = {
    DRAFT: { label: 'Rascunho', labelKey: 'publicTenders.status.draft', dotColor: 'bg-slate-400', textColor: 'text-slate-700', bgColor: 'bg-slate-50', borderColor: 'border-slate-200' },
    AWAITING_PUBLICATION: { label: 'Aguardando publicação', labelKey: 'publicTenders.status.awaitingPublication', dotColor: 'bg-blue-300', textColor: 'text-blue-700', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
    PUBLISHED: { label: 'Publicado', labelKey: 'publicTenders.status.published', dotColor: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
    RECEIVING_PROPOSALS: { label: 'Recebendo propostas', labelKey: 'options.tenderStatus.recebendoPropostas', dotColor: 'bg-emerald-500', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
    ANALYZING_PROPOSALS: { label: 'Analisando propostas', labelKey: 'options.tenderStatus.analisandoPropostas', dotColor: 'bg-amber-400', textColor: 'text-amber-700', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
    AWAITING_DISPUTE: { label: 'Aguardando disputa', labelKey: 'publicTenders.status.awaitingDispute', dotColor: 'bg-orange-300', textColor: 'text-orange-700', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
    IN_DISPUTE: { label: 'Em disputa', labelKey: 'options.tenderStatus.emDisputa', dotColor: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
    IN_ENABLEMENT: { label: 'Em habilitação', labelKey: 'options.tenderStatus.habilitacao', dotColor: 'bg-purple-500', textColor: 'text-purple-700', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
    IN_JUDGMENT: { label: 'Em julgamento', labelKey: 'options.tenderStatus.emJulgamento', dotColor: 'bg-violet-500', textColor: 'text-violet-700', bgColor: 'bg-violet-50', borderColor: 'border-violet-200' },
    IN_APPEAL: { label: 'Em recurso', labelKey: 'options.tenderStatus.emRecurso', dotColor: 'bg-purple-500', textColor: 'text-purple-700', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
    AWARDING: { label: 'Adjudicando', labelKey: 'options.tenderStatus.adjudicando', dotColor: 'bg-sky-400', textColor: 'text-sky-700', bgColor: 'bg-sky-50', borderColor: 'border-sky-200' },
    AWARDED: { label: 'Adjudicado', labelKey: 'options.tenderStatus.adjudicado', dotColor: 'bg-sky-500', textColor: 'text-sky-700', bgColor: 'bg-sky-50', borderColor: 'border-sky-200' },
    // Azul escuro, e nao verde: em emerald ficava igual a RECEIVING_PROPOSALS na
    // lista de processos, e o pregoeiro lia "homologado" como "ainda recebendo".
    // Indigo e o mesmo tom que o status CLOSED_HOMOLOGATED do lote ja usa.
    HOMOLOGATED: { label: 'Homologado', labelKey: 'options.tenderStatus.homologado', dotColor: 'bg-indigo-700', textColor: 'text-indigo-800', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
    FINAL_RESULT: { label: 'Resultado final', labelKey: 'options.tenderStatus.resultadoFinal', dotColor: 'bg-slate-500', textColor: 'text-slate-700', bgColor: 'bg-slate-100', borderColor: 'border-slate-200' },
    CANCELLED: { label: 'Cancelado', labelKey: 'options.tenderStatus.cancelado', dotColor: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
    FAILED: { label: 'Fracassado', labelKey: 'options.tenderStatus.fracassado', dotColor: 'bg-red-400', textColor: 'text-red-700', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
    SUSPENDED: { label: 'Suspenso', labelKey: 'options.tenderStatus.suspenso', dotColor: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
    REVOKED: { label: 'Revogado', labelKey: 'options.tenderStatus.revogado', dotColor: 'bg-rose-500', textColor: 'text-rose-700', bgColor: 'bg-rose-50', borderColor: 'border-rose-200' },
    ANNULLED: { label: 'Anulado', labelKey: 'options.tenderStatus.anulado', dotColor: 'bg-rose-500', textColor: 'text-rose-700', bgColor: 'bg-rose-50', borderColor: 'border-rose-200' },
    UNDER_RECTIFICATION: { label: 'Em retificação', labelKey: 'options.tenderStatus.emRetificacao', dotColor: 'bg-amber-400', textColor: 'text-amber-700', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
    DESERTED: { label: 'Deserta', labelKey: 'options.tenderStatus.deserta', dotColor: 'bg-slate-400', textColor: 'text-slate-600', bgColor: 'bg-slate-50', borderColor: 'border-slate-200' },
};

export function normalizePublicTenderStatus(raw?: string): PublicTenderStatus | undefined {
    if (!raw) return undefined;
    const normalized = String(raw).trim().toUpperCase().replace(/\s+/g, '_');
    return PUBLIC_TENDER_STATUS_SET.has(normalized) ? (normalized as PublicTenderStatus) : undefined;
}

export function normalizeTenderStatus(raw?: string): StatusKey {
    const normalized = normalizePublicTenderStatus(raw);
    return normalized ? PUBLIC_TENDER_STATUS_GROUPS[normalized] : 'draft';
}

export function getStatusMeta(status?: string) {
    return STATUS_META[normalizeTenderStatus(status)];
}

export function getTenderStatusDisplayMeta(status?: string) {
    const normalized = normalizePublicTenderStatus(status);

    if (normalized) {
        return PUBLIC_TENDER_STATUS_META[normalized];
    }

    return {
        label: status || 'Sem status',
        labelKey: '',
        dotColor: 'bg-slate-400',
        textColor: 'text-slate-600',
        bgColor: 'bg-slate-50',
        borderColor: 'border-slate-200',
    };
}

export function getTenderStatusLabel(status?: string) {
    return getTenderStatusDisplayMeta(status).label;
}

/**
 * Status do tender que implicam que a sala de disputa já foi aberta ou está
 * disponível para visualização (mesmo em terminais como CANCELLED/SUSPENDED,
 * onde o histórico precisa ser exibido). Usado por `TabAuction` para decidir
 * se renderiza o conteúdo da sala de disputa ou o placeholder de "ainda não
 * aberta".
 *
 * Em `AWAITING_DISPUTE` o scheduler já avançou o tender mas o pregoeiro ainda
 * não clicou em "iniciar disputa" — mostramos mesmo assim porque o usuário
 * precisa de acesso à sala para poder fazer essa ação.
 */
const DISPUTE_ROOM_VISIBLE_STATUSES = new Set<string>([
    'AWAITING_DISPUTE',
    'IN_DISPUTE',
    'IN_ENABLEMENT',
    'IN_JUDGMENT',
    'IN_APPEAL',
    'AWARDING',
    'AWARDED',
    'HOMOLOGATED',
    'FINAL_RESULT',
    // Administrativos/terminais: mostramos o histórico da disputa que
    // aconteceu antes do estado atual. Usuário vê a timeline/ranking mesmo
    // que a licitação tenha sido cancelada/suspensa/anulada.
    'SUSPENDED',
    'CANCELLED',
    'REVOKED',
    'ANNULLED',
    'FAILED',
    'DESERTED',
]);

/**
 * Retorna true se a sala de disputa deve ser visível para este tender.
 *
 * Regra PRINCIPAL: baseada no status do tender — se o tender já passou da
 * fase de análise de propostas, a sala é visível (mesmo que vazia no início).
 *
 * RN-S2.10: tenders com `disputeType === 'CLOSED'` nao tem sala de disputa
 * — a classificacao e feita direto pelas propostas iniciais. Sempre retorna
 * false pra esse modo, independente do status do tender.
 *
 * Fallback: se o status não puder ser determinado, usa a checagem antiga por
 * `disputeStartDate` para compatibilidade com payloads incompletos.
 */
export function isDisputeRoomVisible(tenderStatus?: string, disputeStartDate?: string | null, disputeType?: string | null): boolean {
    // RN-S2.10: modo Fechado pula a sala de disputa
    if (String(disputeType || '').toUpperCase() === 'CLOSED') {
        return false;
    }
    const normalized = normalizePublicTenderStatus(tenderStatus);
    if (normalized) {
        return DISPUTE_ROOM_VISIBLE_STATUSES.has(normalized);
    }
    // Fallback: payload sem status — confia na data (compat com callers antigos)
    if (!disputeStartDate) return true;
    const ts = Date.parse(disputeStartDate);
    return !Number.isNaN(ts) && ts <= Date.now();
}

export const MODALITY_META: Record<string, { label: string; labelKey: string }> = {
    pregao_eletronico: { label: 'Pregão Eletrônico', labelKey: 'publicTenders.modality.pregao_eletronico' },
    pregao_presencial: { label: 'Pregão Presencial', labelKey: 'publicTenders.modality.pregao_presencial' },
    concorrencia: { label: 'Concorrência', labelKey: 'publicTenders.modality.concorrencia' },
    tomada_precos: { label: 'Tomada de Preços', labelKey: 'publicTenders.modality.tomada_precos' },
    convite: { label: 'Convite', labelKey: 'publicTenders.modality.convite' },
    leilao: { label: 'Leilão', labelKey: 'publicTenders.modality.leilao' },
};

const MODALITY_LOOKUP = (() => {
    const map = new Map<string, string>();
    Object.entries(MODALITY_META).forEach(([key, meta]) => {
        map.set(slugify(key), key);
        map.set(slugify(meta.label), key);
    });
    return map;
})();

export function normalizeTenderModality(raw?: string): string {
    if (!raw) return '';
    const slug = slugify(raw);
    return MODALITY_LOOKUP.get(slug) ?? slug;
}
